import React, { useEffect, useRef, useState } from 'react'
import { useParticipantsList } from './hooks'
import { InputRef, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Participant } from './types'
import { getColumnSearchProps } from '../../../../libs/tableFunctions'
import { FilterValue } from 'antd/es/table/interface'
import { CheckCircleOutlined, CloseSquareOutlined } from '@ant-design/icons'
import { statusTypes } from './constants'
import { Link } from 'react-router-dom'
import { path } from '../../../../libs'
import { BanModal } from '../widgets/banModal/component'

export const ParticipantsList: React.FC = () => {
  const {
    participants,
    loading,
    totalCountOfParticipants,
    onChangeParticipantsList,
  } = useParticipantsList()

  const [modalData, setModalData] = useState({
    isOpen: false,
    id: '',
  })
  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({
    status: ['active'],
  })

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    onChangeParticipantsList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const columns: ColumnsType<Participant> = [
    {
      title: 'ID',
      dataIndex: ['client', 'clientId'],
      defaultSortOrder: 'descend',
      ...getColumnSearchProps<Participant>(
        'client.clientId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (id) => (
        <Link to={path.clients.details(id)} style={{ textDecoration: 'none' }}>
          {id}
        </Link>
      ),
    },
    {
      title: 'Full name',
      dataIndex: ['client', 'fullName'],
      ...getColumnSearchProps<Participant>(
        'client.fullName',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Email',
      dataIndex: ['client', 'email'],
      ...getColumnSearchProps<Participant>(
        'client.email',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Country',
      dataIndex: ['client', 'country'],
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filters: statusTypes,
      defaultFilteredValue: ['active'],
    },
    {
      title: 'Deposit',
      dataIndex: 'isMadeDeposit',
      filters: [
        { text: 'True', value: true },
        { text: 'False', value: false },
      ],
      filterMultiple: false,
      render: (isMadeDeposit: boolean) => (
        <>
          {isMadeDeposit ? (
            <CheckCircleOutlined className={'text-success'} />
          ) : (
            <CloseSquareOutlined className={'text-danger'} />
          )}
        </>
      ),
    },
    {
      title: 'Join',
      dataIndex: 'isPushedJoin',
      filters: [
        { text: 'True', value: true },
        { text: 'False', value: false },
      ],
      filterMultiple: false,
      render: (isPushedJoin: boolean) => (
        <>
          {isPushedJoin ? (
            <CheckCircleOutlined className={'text-success'} />
          ) : (
            <CloseSquareOutlined className={'text-danger'} />
          )}
        </>
      ),
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 150,
      render: (item) => {
        return (
          <div className="d-flex flex-column gap-1">
            <CButton
              color="danger"
              onClick={() =>
                setModalData((prev) => ({
                  ...prev,
                  isOpen: true,
                  id: item.client.clientId,
                }))
              }
            >
              Ban
            </CButton>
          </div>
        )
      },
    },
  ]

  return (
    <CCardBody>
      <Table
        dataSource={participants}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="participantsId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfParticipants,
        }}
        onChange={(pagination, filters, sorter) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters,
          })
        }
      />
      <BanModal
        id={modalData.id}
        isVisible={modalData.isOpen}
        onClose={() =>
          setModalData((prev) => ({
            ...prev,
            isOpen: false,
            id: '',
          }))
        }
      />
    </CCardBody>
  )
}
